import { h } from 'preact'; /** @jsx h */
import { useContext, useEffect, useState } from 'preact/hooks';
import { NotificationContext } from '../context/NotificationContext';
import { fetchProducts } from '../lib/fetchProducts';
import useWindowSize from '../utils/windowSize';

import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import CartView from '../components/CartView/CartView';
import NotificationBanner from '../components/ProductHero/NotificationBanner/NotificationBanner';
import UserPanel from '../components/UserPanel/UserPanel';

import '../styles/shoppingCartPage.scss';

const ShoppingCartPage = ({ slug }) => {
    const { showNotification } = useContext(NotificationContext);

    // States
    const [cart, setCart] = useState([]);
    const [total, setTotal] = useState(0);
    const [isDetails, setIsDetails] = useState(false);
    const [contributionValue, setContributionValue] = useState(0);
    const [viewContribution, setViewContribution] = useState(false);
    const [viewContributionInput, setViewContributionInput] = useState(false);
    const [taxDeduction, setTaxDeduction] = useState(false);
    const [viewTaxDeduction, setViewTaxDeduction] = useState(false);
    const [registrationDetails, setRegistrationDetails] = useState(
        JSON.parse(localStorage.getItem('registrationDetails')) || {}
    );
    const [tabletView, setTabletView] = useState('cart');
    const [errornumber, setErrorNumber] = useState(false);

    // Check if user is on tablet
    const { width: windowWidth } = useWindowSize();
    const isTablet = windowWidth <= 1024;

    // Load cart from local storage when component mounts
    useEffect(() => {
        const loadedCart = JSON.parse(localStorage.getItem('snla-cart')) || [];
        if (loadedCart) {
            setCart(loadedCart);
        }
    }, []);

    // Save cart to local storage whenever it changes
    useEffect(() => {
        const cartToSave = cart.map(item => ({
            id: item.id,
            qty: item.qty,
            shipmentType: item.shipmentType,
            inputPrice: item.inputPrice,
            ...(item?.donation && { donation: item?.donation })
        }));
        localStorage.setItem('snla-cart', JSON.stringify(cartToSave));

        // If cart contains contribution set viewContribution to false
        if (cart.find(item => item.donation)) {
            setViewContribution(false);
            setViewContributionInput(false);
        } else {
            setViewContribution(true);
        }
        // Update the cart count in the header
        window.updateCartCount();
    }, [cart]);

    // Calculate total price of cart
    useEffect(() => {
        let newTotal = 0;
        cart.forEach(item => {
            if (item.price && item.qty) {
                const itemTotal = (item.donation || item.inputPrice || item.price) * item.qty;
                newTotal += itemTotal;
            }
        });
        setTotal(newTotal);
    }, [cart]);

    // Add item to cart
    const addToCart = (id, qty) => {
        // if id is 150 set price in cartitem to contributionValue
        if (id === 150) {
            setCart(prevCart => [...prevCart, { id, qty, donation: contributionValue }]);
        } else {
            setCart(prevCart => [...prevCart, { id, qty }]);
        }
    };

    // Remove item from cart
    const removeFromCart = item => {
        const { id, qty, shipmentType, inputPrice } = item;
        let shipmentTypeNumber = 0;

        if (shipmentType && shipmentType === 'Gavebevis på e-post') {
            shipmentTypeNumber = 1;
        } else if (shipmentType && shipmentType === 'Gavebevis i posten') {
            shipmentTypeNumber = 2;
        }

        const itemElement = document.querySelector(
            `.item-${id}-${qty}-${inputPrice}-${shipmentTypeNumber}`
        );
        itemElement.classList.add('hide');

        setTimeout(() => {
            setCart(
                cart.filter(
                    item =>
                        !(
                            item.id === id &&
                            item.shipmentType === shipmentType &&
                            item.inputPrice === inputPrice
                        )
                )
            );
            if (id === 150) {
                setContributionValue(0);
            }
        }, 300);
    };

    // Update qty of item in cart
    const updateQty = (id, qty, shipmentType, inputPrice) => {
        setCart(
            cart.map(item => {
                if (
                    item.id === id &&
                    (!shipmentType || item.shipmentType === shipmentType) &&
                    item.inputPrice === inputPrice
                ) {
                    return { ...item, qty: qty };
                } else {
                    return item;
                }
            })
        );
    };

    // Fetch product details from backend
    useEffect(() => {
        // Array of id's in cart
        const ids = cart.map(item => item.id);

        // Fetch product details from backend
        const getProductDetails = async () => {
            const response = await fetchProducts(ids);
            const products = response;

            // Add product details to cart
            const updatedCart = cart?.map(cartItem => {
                const productDetails = products?.find(product => product.id === cartItem.id);
                return { ...cartItem, ...productDetails };
            });
            setCart(updatedCart);
            setIsDetails(true);

            // Check if cart contains products with tax deduction
            const taxDeduction = products?.some(product => product.taxDeduction === true);
            setTaxDeduction(taxDeduction);
        };

        if (cart.length > 0) {
            getProductDetails();
        } else {
            setIsDetails(false);
            setTaxDeduction(false);
        }
    }, [cart.length]);

    // Add contribution to cart
    useEffect(() => {
        if (contributionValue > 0) {
            addToCart(150, 1);
        }
    }, [contributionValue]);

    // Handle keypress on contribution input to only allow numbers
    const handleKeyPress = e => {
        const keyCode = e.keyCode || e.which;
        const keyValue = String.fromCharCode(keyCode);
        if (!/^\d+$/.test(keyValue)) {
            e.preventDefault();
        }
    };

    // Matomo eCommerce tracking
    useEffect(() => {
        if (typeof window !== 'undefined' && window._paq) {
            // reset the cart items in Matomo
            window._paq.push(['clearEcommerceCart']);

            // loop through each item to report cart data to Matomo
            cart.forEach(item => {
                const delivery = item.shipmentType === 'Gavebevis på e-post' ? 'D' : 'F';
                const uniqueId = `${item.id}-${delivery}-${
                    item.inputPrice || item.donation || item.price
                }`;

                window._paq.push([
                    'addEcommerceItem',
                    uniqueId,
                    item.name,
                    item.catName,
                    parseFloat(item.inputPrice) || parseFloat(item.donation),
                    parseInt(item.qty)
                ]);
            });

            // push the cart data to Matomo
            window._paq.push(['trackEcommerceCartUpdate', total]);
        }
    }, [cart, total]);

    // Show cart and order view on tablet and smaller screens
    useEffect(() => {
        const orderView = document.querySelector('.ws-order-container');
        const cartView = document.querySelector('.ws-products-container');

        if (!isTablet) {
            cartView.style.display = 'block';
            orderView.style.display = 'block';
        } else {
            window.scrollTo(0, 0);
            if (tabletView === 'cart') {
                cartView.style.display = 'block';
                orderView.style.display = 'none';
            } else if (tabletView === 'order') {
                cartView.style.display = 'none';
                orderView.style.display = 'block';
            } else {
                cartView.style.display = 'block';
                orderView.style.display = 'none';
            }
        }
    }, [isTablet, tabletView]);

    // Check url params fro error message
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const errornum = urlParams.get('errornum');

        if (error) {
            if (!errornum) {
                // eslint-disable-next-line
                console.log('API ERROR: ', error);
                showNotification(
                    'error',
                    `${error}. Venligst prøv igjen senere. Hvis problemet vedvarer, venligst ta kontakt med oss.`,
                    '',
                    '',
                    0
                );
            }
            if (errornum === '0') {
                // eslint-disable-next-line
                console.log('API ERROR: ', error);
                showNotification(
                    'error',
                    'En feil har oppstått. Venligst prøv igjen senere. Hvis problemet vedvarer, venligst ta kontakt med oss.',
                    '',
                    '',
                    0
                );
            }
            if (errornum === '1') {
                // eslint-disable-next-line
                console.log('API ERROR: ', error);
                showNotification(
                    'success',
                    'Du valgte å avbryte transaksjonen. Ingen produkter er bestilt. Foreta en ny bestilling hvis du ønsker å kjøpe noe.',
                    '',
                    '',
                    0
                );
            }
            if (errornum === '99') {
                setErrorNumber(99);
                // eslint-disable-next-line
                console.log('API ERROR: ', error);
                showNotification(
                    'error',
                    'En feil har oppstått etter din betaling gikk igjennom. Venligst ta kontakt med oss på tlf: 64 90 43 00.',
                    '',
                    '',
                    0
                );
            }
        }
    }, []);

    return (
        <div className="ws-cart">
            <Breadcrumbs slug={slug} />
            <div className="ws-cart-main">
                <UserPanel
                    cart={cart}
                    total={total}
                    isDetails={isDetails}
                    registrationDetails={registrationDetails}
                    setRegistrationDetails={setRegistrationDetails}
                    tabletView={tabletView}
                    setTabletView={setTabletView}
                    isTablet={isTablet}
                    errorNumber={errornumber}
                />
                <CartView
                    cart={cart}
                    total={total}
                    isDetails={isDetails}
                    taxDeduction={taxDeduction}
                    viewContribution={viewContribution}
                    viewContributionInput={viewContributionInput}
                    viewTaxDeduction={viewTaxDeduction}
                    removeFromCart={removeFromCart}
                    updateQty={updateQty}
                    setContributionValue={setContributionValue}
                    setViewContributionInput={setViewContributionInput}
                    setViewTaxDeduction={setViewTaxDeduction}
                    handleKeyPress={handleKeyPress}
                    tabletView={tabletView}
                    setTabletView={setTabletView}
                    isTablet={isTablet}
                />
                <NotificationBanner />
            </div>
        </div>
    );
};

export default ShoppingCartPage;
